/**
 * Liquid Glass Design Token System
 *
 * Apple-inspired Liquid Glass material properties for translucent UI surfaces.
 * Emphasizes lensing, specular highlights, soft shadows, and adaptive tinting.
 *
 * Reference values:
 * - Blur: ~22% → 24px
 * - Translucency: 50%
 * - Dark: 42%
 * - Shadow: Neutral 50%
 * - Specular: ON
 *
 * @see https://developer.apple.com/design/human-interface-guidelines/materials
 */

// ============================================================================
// Material Variants
// ============================================================================

export const liquidGlass = {
  /**
   * Regular material — primary glass surfaces (cards, panels, sidebars).
   * Higher opacity for readability over complex backgrounds.
   */
  regular: {
    light: {
      background: 'rgba(255, 255, 255, 0.50)',
      backgroundHover: 'rgba(255, 255, 255, 0.62)',
      blur: 'blur(24px) saturate(180%)',
      border: '0.5px solid rgba(255, 255, 255, 0.35)',
      borderHover: '0.5px solid rgba(255, 255, 255, 0.50)',
      shadow: '0 4px 30px rgba(0, 0, 0, 0.05)',
      shadowHover: '0 8px 40px rgba(0, 0, 0, 0.08)',
      /** Specular highlight — white inset at top edge simulating light refraction */
      specular: 'inset 0 0.5px 0 0 rgba(255, 255, 255, 0.6)',
      /** Inner shadow — subtle depth at bottom edge */
      innerShadow: 'inset 0 -0.5px 0 0 rgba(0, 0, 0, 0.05)',
    },
    dark: {
      background: 'rgba(28, 28, 30, 0.12)',
      backgroundHover: 'rgba(28, 28, 30, 0.20)',
      blur: 'blur(24px) saturate(180%)',
      border: '0.5px solid rgba(255, 255, 255, 0.04)',
      borderHover: '0.5px solid rgba(255, 255, 255, 0.08)',
      shadow: '0 4px 30px rgba(0, 0, 0, 0.08)',
      shadowHover: '0 8px 40px rgba(0, 0, 0, 0.14)',
      specular: 'inset 0 0.5px 0 0 rgba(255, 255, 255, 0.05)',
      innerShadow: 'inset 0 -0.5px 0 0 rgba(0, 0, 0, 0.08)',
    },
  },

  /**
   * Clear material — ultra-translucent surfaces (tooltips, overlays, subtle backdrops).
   * Lower opacity reveals more background content.
   */
  clear: {
    light: {
      background: 'rgba(255, 255, 255, 0.25)',
      backgroundHover: 'rgba(255, 255, 255, 0.35)',
      blur: 'blur(16px) saturate(150%)',
      border: '0.5px solid rgba(255, 255, 255, 0.2)',
      borderHover: '0.5px solid rgba(255, 255, 255, 0.3)',
      shadow: '0 2px 16px rgba(0, 0, 0, 0.04)',
      specular: 'inset 0 0.5px 0 0 rgba(255, 255, 255, 0.4)',
    },
    dark: {
      background: 'rgba(28, 28, 30, 0.25)',
      backgroundHover: 'rgba(28, 28, 30, 0.35)',
      blur: 'blur(16px) saturate(150%)',
      border: '0.5px solid rgba(255, 255, 255, 0.08)',
      borderHover: '0.5px solid rgba(255, 255, 255, 0.14)',
      shadow: '0 2px 16px rgba(0, 0, 0, 0.1)',
      specular: 'inset 0 0.5px 0 0 rgba(255, 255, 255, 0.1)',
    },
  },

  /**
   * Thick material — high-opacity surfaces for nav bars and sidebars.
   * Prioritizes readability while maintaining glass aesthetics.
   */
  thick: {
    light: {
      background: 'rgba(255, 255, 255, 0.78)',
      backgroundHover: 'rgba(255, 255, 255, 0.88)',
      blur: 'blur(24px) saturate(180%)',
      border: '0.5px solid rgba(255, 255, 255, 0.45)',
      borderHover: '0.5px solid rgba(255, 255, 255, 0.55)',
      shadow: '0 1px 12px rgba(0, 0, 0, 0.04)',
      specular: 'inset 0 0.5px 0 0 rgba(255, 255, 255, 0.7)',
    },
    dark: {
      background: 'rgba(28, 28, 30, 0.62)',
      backgroundHover: 'rgba(28, 28, 30, 0.72)',
      blur: 'blur(24px) saturate(180%)',
      border: '0.5px solid rgba(255, 255, 255, 0.14)',
      borderHover: '0.5px solid rgba(255, 255, 255, 0.20)',
      shadow: '0 1px 12px rgba(0, 0, 0, 0.12)',
      specular: 'inset 0 0.5px 0 0 rgba(255, 255, 255, 0.18)',
    },
  },
} as const;

// ============================================================================
// Corner Radius System (Concentric Geometry)
// ============================================================================
// Inner radii should be: outer radius - padding
// e.g., Card (md=16) with 16px padding → inner items use sm=12

export const liquidRadius = {
  /** Small controls: chips, tags, small badges */
  xs: 8,
  /** Buttons, inputs, menu items */
  sm: 12,
  /** Cards, panels, alerts */
  md: 16,
  /** Modals, large surfaces */
  lg: 20,
  /** Full-sheet overlays, drawers */
  xl: 24,
  /** Capsule / pill buttons (radius > height/2) */
  pill: 9999,
} as const;

// ============================================================================
// Spacing System (8pt Grid)
// ============================================================================

export const liquidSpacing = {
  /** 4px — half unit, minimal gaps */
  xxs: 4,
  /** 8px — 1 unit, compact spacing */
  xs: 8,
  /** 12px — 1.5 units, tight content spacing */
  sm: 12,
  /** 16px — 2 units, standard padding */
  md: 16,
  /** 24px — 3 units, section gaps */
  lg: 24,
  /** 32px — 4 units, large section gaps */
  xl: 32,
  /** 48px — 6 units, page-level spacing */
  xxl: 48,
} as const;

// ============================================================================
// Surface Radius — Interior Chrome (smaller than liquidRadius.xs)
// ============================================================================
// `liquidRadius` ladders from 8 → 24 for primary surfaces (chips, cards,
// modals). v3 design pass introduced a sub-8px tier for tiny interior chrome
// (color swatch indicators, sparkline bars, empty-state icon discs, compact
// KPI tiles) where `liquidRadius.xs` (8px) is too rounded for the element's
// size. Each entry is named for its DESIGN purpose, not its pixel value, so
// future scale changes ripple through cleanly.
//
// Usage: `borderRadius: surfaceRadius.colorSwatch` (number → MUI accepts px).

export const surfaceRadius = {
  /** 2px — sparkline / data-bar radius (3-px wide vertical bars in KPI cards) */
  sparkBar: 2,
  /** 3px — tiny color-indicator swatches (12-14px squares in list rows) */
  colorSwatch: 3,
  /** 4px — small tag chip / template-picker swatch (slightly larger than colorSwatch) */
  tagChip: 4,
  /** 6px — inline highlighted note box inside a panel section */
  inlineNote: 6,
  /** 9px — compact KPI tile (smaller than liquidRadius.sm=12 to read as a sub-element of its parent card) */
  statTile: 9,
  /** 10px — compact section panel wrapper (one notch below liquidRadius.sm=12) */
  sectionPanel: 10,
  /** 14px — soft-square icon disc (60×60 empty-state illustrations); between liquidRadius.sm=12 and md=16 */
  iconDisc: 14,
} as const;

// ============================================================================
// Floating Action Clearance — Spacing for fixed-position overlays
// ============================================================================
// Reserve right-margin on panel footers and toolbars so action buttons don't
// sit underneath the floating Astor launcher (position: fixed; bottom: 16;
// right: 16; ~56px button + 16px gap = ~88px footprint). Use as theme.spacing
// units: `sx={{ mr: floatingActionClearance.astorLauncher }}` → 10 → 80px.

export const floatingActionClearance = {
  /** 10 (= 80px) — clears the floating Astor launcher in the bottom-right corner */
  astorLauncher: 10,
} as const;

// ============================================================================
// Layered Shadow System (Apple-style depth)
// ============================================================================
// Apple uses three visual layers: Highlight (specular), Shadow (depth), Illumination (material)

export const liquidShadows = {
  /** No elevation — flush surfaces */
  0: 'none',
  /** Subtle — cards at rest, inputs */
  1: '0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.06)',
  /** Low — hover states, slight lift */
  2: '0 2px 8px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.08)',
  /** Medium — dropdowns, menus, popovers */
  3: '0 4px 16px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.06)',
  /** High — modals, dialogs, panels */
  4: '0 8px 30px rgba(0, 0, 0, 0.10), 0 4px 10px rgba(0, 0, 0, 0.06)',
  /** Very high — toasts, notifications */
  5: '0 16px 48px rgba(0, 0, 0, 0.12), 0 6px 16px rgba(0, 0, 0, 0.08)',
  /** Maximum — full overlays */
  6: '0 24px 64px rgba(0, 0, 0, 0.16), 0 8px 24px rgba(0, 0, 0, 0.10)',
} as const;

export const liquidShadowsDark = {
  0: 'none',
  1: '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.16)',
  2: '0 2px 8px rgba(0, 0, 0, 0.16), 0 1px 3px rgba(0, 0, 0, 0.20)',
  3: '0 4px 16px rgba(0, 0, 0, 0.20), 0 2px 6px rgba(0, 0, 0, 0.16)',
  4: '0 8px 30px rgba(0, 0, 0, 0.24), 0 4px 10px rgba(0, 0, 0, 0.16)',
  5: '0 16px 48px rgba(0, 0, 0, 0.28), 0 6px 16px rgba(0, 0, 0, 0.20)',
  6: '0 24px 64px rgba(0, 0, 0, 0.32), 0 8px 24px rgba(0, 0, 0, 0.24)',
} as const;

// ============================================================================
// Specular Highlight Helpers
// ============================================================================

/**
 * Get the combined box-shadow for a liquid glass surface (specular + depth shadow).
 * Includes top-edge specular highlight and bottom-edge inner shadow.
 */
export function getLiquidGlassShadow(isDark: boolean, elevation: keyof typeof liquidShadows = 1): string {
  const material = isDark ? liquidGlass.regular.dark : liquidGlass.regular.light;
  const shadow = isDark ? liquidShadowsDark[elevation] : liquidShadows[elevation];

  if (shadow === 'none') {
    return `${material.specular}, ${material.innerShadow}`;
  }

  return `${material.specular}, ${material.innerShadow}, ${shadow}`;
}

/**
 * Get glass material properties for a given variant and mode.
 */
export function getLiquidGlassMaterial(isDark: boolean, variant: 'regular' | 'clear' | 'thick' = 'regular') {
  return isDark ? liquidGlass[variant].dark : liquidGlass[variant].light;
}

// ============================================================================
// Type Exports
// ============================================================================

export type LiquidGlassVariant = 'regular' | 'clear' | 'thick';
export type LiquidRadiusKey = keyof typeof liquidRadius;
export type LiquidSpacingKey = keyof typeof liquidSpacing;
export type LiquidShadowLevel = keyof typeof liquidShadows;
export type SurfaceRadiusKey = keyof typeof surfaceRadius;
export type FloatingActionClearanceKey = keyof typeof floatingActionClearance;
